import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { WaveSine, LockKey, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function errText(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <WaveSine size={26} weight="duotone" className="text-[#ccff00]" />
          <span className="font-display text-xl font-bold tracking-tight">TUNEAMPLIFIER</span>
        </Link>
        <div className="border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8" data-testid="reset-password-card">
          {!done ? (
            <>
              <h1 className="font-display text-3xl font-bold">New password.</h1>
              <p className="text-sm text-zinc-400 mt-1 mb-6">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="reset-password-input" type="password" required placeholder="New password (min. 8 characters)" value={password}
                    onChange={e => setPassword(e.target.value)} className="pl-9 bg-zinc-950 border-zinc-800" />
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full btn-neon rounded-full font-display font-semibold h-11">
                  {loading ? 'Saving…' : 'Reset password'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-done-panel">
              <CheckCircle size={40} weight="fill" className="text-[#ccff00] mx-auto" />
              <h1 className="font-display text-2xl font-bold mt-4">Password reset.</h1>
              <p className="text-sm text-zinc-400 mt-2">You can log in with your new password now.</p>
              <Button data-testid="reset-goto-login" onClick={() => nav('/login')} className="btn-neon rounded-full mt-6 h-11 px-6 font-display font-semibold">
                Go to log in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
